import styles from './EntityHistory.module.scss';

import {
  Divider,
  Tab,
  Tabs,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import clsx from 'clsx';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@work4all/data/lib/hooks/use-permissions';
import { remToPx } from '@work4all/data/lib/hooks/useRemToPx';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { CommentEditorWithAddButton } from './components/CommentEditorWithAddButton';
import { EventsViewStateProvider } from './components/events-view-state-context';
import { EventView } from './components/EventView';
import {
  EntityHistoryContext,
  EntityHistoryContextValue,
} from './entity-history-context';
import { groupEventsByDate } from './group-events-by-date';
import { useHistoryEvents } from './use-history-events';
import { textEditorCommentKeydown } from './utils/text-editor-comment-keydown';

export interface EntityHistoryProps {
  entity: Entities;
  id: string | number;
  fitContainer?: boolean;
  className?: string;
  breadcrumbTitle?: string;
  fullWidth?: boolean;
  fullCommentEditor?: boolean;
  withTabs?: boolean;
  disableAddingComment?: boolean;
  children?: React.ReactElement<any>;
}
type tab = 'all' | 'comments' | 'events';

export function EntityHistory(props: EntityHistoryProps) {
  const {
    entity: entityType,
    id,
    fitContainer = true,
    fullWidth = false,
    fullCommentEditor = false,
    withTabs = false,
    disableAddingComment = false,
    children,
  } = props;

  const { events, addComment, comment, setComment } = useHistoryEvents(
    entityType,
    id
  );

  const { t } = useTranslation();
  const { canEdit } = usePermissions();
  const entity = entityToHistoryEventEntity(entityType);
  const canEditEntity = canEdit({ entity, record: null });

  const [isAddCommentInputActive, setIsAddCommentInputActive] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  const debounceTimeoutRef = useRef(null);
  const shouldOmitBlurRef = useRef(false);

  const onAfterDelete = () => {
    events.refetch();
  };

  const [selectedTab, setSelectedTab] = useState<tab>('all');
  const { filteredEvents, numberOfEvents, numberOfComments } = useMemo(() => {
    const allEvents = events.data.filter(
      (event) => event.__typename !== 'ChildItemCreated'
    );
    const eventsList = allEvents.filter(
      (event) =>
        event.__typename === 'CreateEvent' || event.__typename === 'ChangeEvent'
    );
    const commentsList = allEvents.filter(
      (event) => event.__typename === 'CommentEvent'
    );

    const numberOfEvents = eventsList.length;
    const numberOfComments = commentsList.length;

    let filteredEvents;
    switch (selectedTab) {
      case 'all':
        filteredEvents = allEvents;
        break;
      case 'events':
        filteredEvents = eventsList;
        break;
      case 'comments':
        filteredEvents = commentsList;
        break;
    }

    return { filteredEvents, numberOfEvents, numberOfComments };
  }, [events.data, selectedTab]);

  const eventsGroupedByDate = useMemo(
    () => groupEventsByDate(filteredEvents, t),
    [t, filteredEvents]
  );

  const context = useMemo<EntityHistoryContextValue>(
    () => ({ entity }),
    [entity]
  );

  const handleEditorBlur = useCallback(
    (e: { originalEvent?: { relatedTarget?: Element } }) => {
      const clickedOnMentionPopover =
        e.originalEvent?.relatedTarget?.classList?.contains('MuiPaper-root');

      if (clickedOnMentionPopover) {
        shouldOmitBlurRef.current = true;
        return;
      }

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        if (!shouldOmitBlurRef.current) {
          setIsAddCommentInputActive(false);
        }
        shouldOmitBlurRef.current = false;
        debounceTimeoutRef.current = null;
      }, 300);
    },
    [setIsAddCommentInputActive]
  );

  const isDownMd = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md')
  );

  const canAddOrInsertImage = entity === Entities.ticket;

  const handleCommentSubmit = useCallback(
    async (htmlContent?: string) => {
      if (isSubmittingComment) return;

      setIsSubmittingComment(true);
      try {
        await addComment(!htmlContent ? comment : htmlContent);
      } finally {
        setIsSubmittingComment(false);
      }
    },
    [addComment, comment, isSubmittingComment]
  );

  return (
    <EntityHistoryContext.Provider value={context}>
      {fullCommentEditor && (
        <div className={styles.editor}>
          <CommentEditorWithAddButton
            onSubmit={handleCommentSubmit}
            disabled={
              disableAddingComment || !canEditEntity || isSubmittingComment
            }
            value={comment}
            setValue={setComment}
            canAddOrInsertImage={canAddOrInsertImage}
            fullCommentEditor={fullCommentEditor}
            editorConf={{
              multiLine: false,
              height: remToPx(isDownMd ? 1.5 : 8),
              heightMin: remToPx(isDownMd ? 1.5 : 8),
              heightMax: remToPx(isDownMd ? 8 : 16),
              events: {
                keydown: function (e: KeyboardEvent) {
                  textEditorCommentKeydown(e, {
                    edit: () => {
                      const htmlContent = this.html.get();
                      if (htmlContent && !isSubmittingComment) {
                        handleCommentSubmit(htmlContent);
                      }
                    },
                    newLine: () => {
                      this.cursor.enter();
                    },
                  });
                },
              },
            }}
            hideToolbar={isDownMd}
          />
        </div>
      )}

      {withTabs && (
        <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)}>
          <Tab value="all" label={t('COMMON.ALL')} />
          <Tab
            value="comments"
            label={`${t('COMMON.COMMENTS')} (${numberOfComments})`}
          />
          <Tab
            value="events"
            label={`${t('COMMON.EVENTS')} (${numberOfEvents})`}
          />
        </Tabs>
      )}

      <div
        className={clsx(styles.root, props.className, {
          [styles.fullWidth]: fullWidth,
        })}
      >
        <div
          className={clsx(styles.container, {
            [styles.fitContainer]: fitContainer,
          })}
        >
          {children}
          {events.loading ? (
            <div className={styles.loading}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {!fullCommentEditor && (
                <div
                  className={clsx(styles.header, {
                    [styles.headerButtonPadding]:
                      isAddCommentInputActive || comment,
                  })}
                >
                  <CommentEditorWithAddButton
                    onSubmit={handleCommentSubmit}
                    disabled={
                      disableAddingComment ||
                      !canEditEntity ||
                      isSubmittingComment
                    }
                    value={comment}
                    setValue={setComment}
                    canAddOrInsertImage={canAddOrInsertImage}
                    fullCommentEditor={!isAddCommentInputActive}
                    editorConf={{
                      autofocus: false,
                      multiLine: false,
                      placeholderText: t('MASK.ADD_COMMENT'),
                      height: 52,
                      heightMin: 150,
                      heightMax: 300,
                      events: {
                        focus: () => {
                          setIsAddCommentInputActive(true);
                        },
                        blur: handleEditorBlur,
                        keydown: function (e: KeyboardEvent) {
                          textEditorCommentKeydown(e, {
                            edit: () => {
                              const htmlContent = this.html.get();
                              if (htmlContent && !isSubmittingComment) {
                                handleCommentSubmit(htmlContent);
                              }
                            },
                            newLine: () => {
                              this.cursor.enter();
                            },
                          });
                        },
                      },
                      toolbarButtons: {
                        moreText: {
                          buttons: [
                            'bold',
                            'underline',
                            'italic',
                            'strikeThrough',
                            'clearFormatting',
                            'insertImage',
                          ],
                          buttonsVisible: 6,
                        },
                      },
                    }}
                    hideToolbar={isDownMd || !isAddCommentInputActive}
                  />
                </div>
              )}

              <div className={styles.events}>
                {events.data.length > 0 ? (
                  eventsGroupedByDate.map(({ label, events }, i) => (
                    <React.Fragment key={`${label}-${i}`}>
                      <Divider>{label}</Divider>
                      <EventsViewStateProvider>
                        {events.map((event) => (
                          <EventView
                            key={event.id}
                            event={event}
                            canAddOrInsertImage={canAddOrInsertImage}
                            onAfterDelete={onAfterDelete}
                          />
                        ))}
                      </EventsViewStateProvider>
                    </React.Fragment>
                  ))
                ) : (
                  <Typography variant="caption">
                    {t('COMMON.NO_DATA')}
                  </Typography>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </EntityHistoryContext.Provider>
  );
}

function entityToHistoryEventEntity(entity: Entities) {
  switch (entity) {
    case Entities.reViewModel:
      return Entities.inboundInvoice;
    default:
      return entity;
  }
}
